import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Loading from '../../components/shared/Loading/Loading';
import Button from '../../components/shared/Button/Button';
import Modal from '../../components/shared/Modal/Modal';
import StatusBadge from '../../components/shared/StatusBadge/StatusBadge';
import { useConfirm } from '../../components/shared/ConfirmDialog/ConfirmDialog';
import {
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormHint,
  FormActions,
  ErrorMessage,
} from '../../components/shared/Form/Form.styles';
import { Panel, Notice } from '../sales/Sales.styles';
import { Pager, EmptyBox } from '../sales/SalesList.styles';
import { ItemList, ItemCard, EmpresaLine, LinkButton } from '../../components/promoters/promoters.styles';
import { endedByLabel, linkStatus } from '../../components/promoters/promoterLabels';
import { endPromoterLink, listPromoterEmpresas, requestPromoterLink } from '../../services/promoterApi';
import { formatTimestamp } from '../../utils/dates';

const MESSAGE_MAX = 500;
const PAGE_SIZE = 12;

const LINK_FILTERS = [
  { value: 'all', label: 'Todas' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'accepted', label: 'Aceptadas' },
  { value: 'rejected', label: 'Rechazadas' },
  { value: 'revoked', label: 'Revocadas' },
];

const Logo = ({ empresa }) => (
  <EmpresaLine>
    <span className="logo" aria-hidden="true">
      {empresa.logoUrl ? <img src={empresa.logoUrl} alt="" /> : (empresa.businessName || '?').charAt(0).toUpperCase()}
    </span>
    <span className="names">
      <strong>{empresa.businessName}</strong>
      {empresa.city && <span>{empresa.city}</span>}
    </span>
  </EmpresaLine>
);

// Modal de la marca para solicitar vender las actividades de una empresa.
const RequestModal = ({ empresa, mode, onClose, onSent, onError }) => {
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSend = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const data = await requestPromoterLink(mode, empresa.id, message.trim());
      onSent(data.link, data.message);
    } catch (err) {
      if (onError && onError(err)) return;
      setError(err.message);
      setBusy(false);
      // 409: ya había una solicitud pendiente o aceptada -> la lista se actualiza al cerrar
      if (err.status === 409) onSent(err.data && err.data.link, null, true);
    }
  };

  return (
    <Modal title={`Vender las actividades de ${empresa.businessName}`} onClose={busy ? null : onClose} maxWidth="500px" labelledBy="req-title">
      <form onSubmit={handleSend}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 14 }}>
          Le enviamos tu solicitud a la empresa. Si la <strong>acepta</strong>, podrás compartir el enlace de sus actividades y ganar
          tu comisión por cada venta hecha con él. Puedes retirar la solicitud mientras esté pendiente.
        </p>
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        <FormGroup>
          <FormLabel htmlFor="link-message">Mensaje para la empresa (opcional)</FormLabel>
          <FormTextarea
            id="link-message"
            rows={3}
            maxLength={MESSAGE_MAX}
            value={message}
            disabled={busy}
            placeholder="Preséntate: dónde vas a compartir sus actividades, tu experiencia…"
            onChange={(e) => setMessage(e.target.value)}
          />
          <FormHint>{message.length}/{MESSAGE_MAX}</FormHint>
        </FormGroup>
        <FormActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button type="submit" disabled={busy}>{busy ? 'Enviando…' : 'Enviar solicitud'}</Button>
        </FormActions>
      </form>
    </Modal>
  );
};

// Pestaña "Empresas": mis relaciones (solicitudes y su estado) y buscador de
// empresas a las que pedir vender sus actividades.
const EmpresasTab = ({ mode, links, onReloadLinks, onChanged, onError }) => {
  const [filter, setFilter] = useState('all');
  const [flash, setFlash] = useState('');
  const [actionError, setActionError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const { confirm, dialog } = useConfirm();

  // buscador
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(true);
  const [searchError, setSearchError] = useState('');
  const [requesting, setRequesting] = useState(null);
  const errorRef = useRef(onError);
  errorRef.current = onError;

  const runSearch = useCallback(async () => {
    setSearching(true);
    setSearchError('');
    try {
      const data = await listPromoterEmpresas(mode, { search, page, limit: PAGE_SIZE });
      setResult(data);
    } catch (err) {
      if (errorRef.current && errorRef.current(err)) return;
      setSearchError(err.message);
    } finally {
      setSearching(false);
    }
  }, [mode, search, page]);

  useEffect(() => { runSearch(); }, [runSearch]);

  const items = (links && links.items) || [];
  const shown = filter === 'all' ? items : items.filter((l) => l.status === filter);

  const handleEnd = async (link) => {
    const pending = link.status === 'pending';
    const ok = await confirm({
      title: pending ? '¿Retirar tu solicitud?' : `¿Dejar de vender a ${link.empresa.businessName}?`,
      message: pending
        ? `Se cancela tu solicitud a ${link.empresa.businessName}. Podrás volver a enviarla más adelante.`
        : `Tus enlaces de las actividades de ${link.empresa.businessName} dejarán de atribuirte ventas nuevas. Las ventas que ya hiciste siguen en tus cortes.`,
      confirmLabel: pending ? 'Retirar solicitud' : 'Dejar de vender',
      cancelLabel: 'Conservar',
      danger: true,
    });
    if (!ok) return;
    setBusyId(link.id);
    setActionError('');
    setFlash('');
    try {
      await endPromoterLink(mode, link.id);
      setFlash(pending ? 'Solicitud retirada.' : 'Dejaste de vender las actividades de esa empresa.');
      onReloadLinks();
      onChanged();
      runSearch();
    } catch (err) {
      if (onError && onError(err)) return;
      setActionError(err.message);
      onReloadLinks();
    } finally {
      setBusyId(null);
    }
  };

  const handleSent = (link, message, conflict) => {
    onReloadLinks();
    onChanged();
    runSearch();
    if (conflict) return; // el modal sigue abierto mostrando el error del 409
    setRequesting(null);
    setFlash(message || 'Solicitud enviada. La empresa debe aceptarla para que puedas vender sus actividades.');
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchText.trim());
  };

  const canRequest = (e) => !e.linkStatus || e.linkStatus === 'rejected' || e.linkStatus === 'revoked';
  const totalPages = (result && result.totalPages) || 1;

  return (
    <>
      {flash && <Notice role="status">{flash}</Notice>}
      {actionError && <ErrorMessage role="alert">{actionError}</ErrorMessage>}

      <Panel>
        <h2>Mis empresas</h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 12 }}>
          Para compartir las actividades de una empresa, ella debe <strong>aceptar tu solicitud</strong>.
        </p>
        <div className="filters" role="group" aria-label="Filtrar solicitudes" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {LINK_FILTERS.map((f) => (
            <Button
              key={f.value}
              size="small"
              variant={filter === f.value ? 'primary' : 'ghost'}
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {!links || links.loading ? (
          <Loading text="Cargando tus empresas..." />
        ) : links.error ? (
          <ErrorMessage role="alert">
            {links.error} <LinkButton type="button" onClick={onReloadLinks}>Reintentar</LinkButton>
          </ErrorMessage>
        ) : shown.length === 0 ? (
          <EmptyBox>
            <h3>{items.length === 0 ? 'Aún no has pedido vender ninguna empresa' : 'No hay solicitudes con este filtro'}</h3>
            <p>{items.length === 0 ? 'Busca una empresa abajo y envíale tu solicitud.' : 'Cambia el filtro para ver otras.'}</p>
          </EmptyBox>
        ) : (
          <ItemList>
            {shown.map((link) => {
              const state = linkStatus(link.status);
              const by = endedByLabel(link.endedBy);
              return (
                <ItemCard key={link.id} data-muted={link.status === 'rejected' || link.status === 'revoked'}>
                  <div className="head">
                    <Logo empresa={link.empresa} />
                    <StatusBadge status={state.status}>{state.label}</StatusBadge>
                  </div>
                  <div className="meta">
                    Solicitada el {formatTimestamp(link.requestedAt)}
                    {link.respondedAt ? ` · respondida el ${formatTimestamp(link.respondedAt)}` : ''}
                    {link.revokedAt ? ` · terminada el ${formatTimestamp(link.revokedAt)}${by ? ` por ${by}` : ''}` : ''}
                  </div>
                  {link.message && <div className="note"><strong>Tu mensaje:</strong> {link.message}</div>}
                  {link.responseNote && <div className="note"><strong>Respuesta de la empresa:</strong> {link.responseNote}</div>}
                  {link.status === 'accepted' && (
                    <div className="meta">Ya puedes compartir sus actividades desde la pestaña «Mis actividades».</div>
                  )}
                  <div className="actions">
                    {(link.status === 'pending' || link.status === 'accepted') && (
                      <Button size="small" variant="ghost" disabled={busyId === link.id} onClick={() => handleEnd(link)}>
                        {link.status === 'pending' ? 'Retirar solicitud' : 'Dejar de vender'}
                      </Button>
                    )}
                    {(link.status === 'rejected' || link.status === 'revoked') && (
                      <Button size="small" variant="secondary" onClick={() => setRequesting(link.empresa)}>
                        Volver a solicitar
                      </Button>
                    )}
                    {link.empresa.slug && (
                      <Link to={`/empresas/${link.empresa.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button size="small" variant="ghost">Ver su perfil</Button>
                      </Link>
                    )}
                  </div>
                </ItemCard>
              );
            })}
          </ItemList>
        )}
      </Panel>

      <Panel>
        <h2>Buscar empresas</h2>
        <form onSubmit={submitSearch} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <FormGroup style={{ flex: '1 1 220px', marginBottom: 0, minWidth: 0 }}>
            <FormLabel htmlFor="empresa-search">Nombre o ciudad</FormLabel>
            <FormInput
              id="empresa-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Ej. Tulum"
              autoComplete="off"
            />
          </FormGroup>
          <div style={{ alignSelf: 'flex-end' }}>
            <Button type="submit" variant="secondary">Buscar</Button>
          </div>
        </form>

        {searchError && (
          <ErrorMessage role="alert">
            {searchError} <LinkButton type="button" onClick={runSearch}>Reintentar</LinkButton>
          </ErrorMessage>
        )}

        {searching && !result ? (
          <Loading text="Buscando empresas..." />
        ) : result && result.empresas.length === 0 ? (
          <EmptyBox>
            <h3>No encontramos empresas</h3>
            <p>Solo aparecen empresas verificadas. Prueba con otro nombre o ciudad.</p>
          </EmptyBox>
        ) : (
          <>
            <ItemList aria-busy={searching}>
              {((result && result.empresas) || []).map((empresa) => {
                const state = empresa.linkStatus ? linkStatus(empresa.linkStatus) : null;
                return (
                  <ItemCard key={empresa.id}>
                    <div className="head">
                      <Logo empresa={empresa} />
                      {state && <StatusBadge status={state.status}>{state.label}</StatusBadge>}
                    </div>
                    <div className="actions">
                      {canRequest(empresa) ? (
                        <Button size="small" onClick={() => setRequesting(empresa)}>
                          {empresa.linkStatus ? 'Volver a solicitar' : 'Solicitar vender sus actividades'}
                        </Button>
                      ) : (
                        <span className="meta">
                          {empresa.linkStatus === 'pending' ? 'Solicitud enviada: esperando respuesta de la empresa.' : 'Ya vendes sus actividades.'}
                        </span>
                      )}
                    </div>
                  </ItemCard>
                );
              })}
            </ItemList>
            {totalPages > 1 && (
              <Pager>
                <Button size="small" variant="ghost" disabled={page <= 1 || searching} onClick={() => setPage(page - 1)}>← Anteriores</Button>
                <span>Página {page} de {totalPages}</span>
                <Button size="small" variant="ghost" disabled={page >= totalPages || searching} onClick={() => setPage(page + 1)}>Siguientes →</Button>
              </Pager>
            )}
          </>
        )}
      </Panel>

      {requesting && (
        <RequestModal
          empresa={requesting}
          mode={mode}
          onClose={() => setRequesting(null)}
          onSent={handleSent}
          onError={onError}
        />
      )}
      {dialog}
    </>
  );
};

export default EmpresasTab;
